import { useState } from 'react';

import { BsCpu } from 'react-icons/bs';
import { HiSparkles } from 'react-icons/hi2';

import Button from '#globalComponents/Button/Button';
import Checkbox from '#globalComponents/Checkbox/Checkbox';
import FormField from '#globalComponents/Forms/FormField';
import Textarea from '#globalComponents/Forms/Textarea';
import Panel from '#globalComponents/Overlay/Panel';
import Popover from '#globalComponents/Overlay/Popover';
import RequireCapability from '#globalComponents/RequireCapability/RequireCapability';
import ActionScopeButton from '#globalComponents/Table/actions/ActionScopeButton';
import { CONTACT_SELECTION_HINT } from '#lib/constants/entityLabels';

import { useLeadUI } from '../../../../../contexts/LeadUIContext';
import { useActionScope } from '../../../../../hooks/useActionScope';
import { useStartCompanyAnalysis } from '../../../../../models/companyAnalysis/mutations';

export const AiSearchForm = () => {
	const actionScope = useActionScope();
	const { leadActionTarget, hasContactSelection } = useLeadUI();
	const { scope, count, prospectQueryId } = actionScope;
	const startAnalysisMutation = useStartCompanyAnalysis();

	const [isOpen, setIsOpen] = useState(false);
	const [parameters, setParameters] = useState('');
	const [addSocialMediaParams, setAddSocialMediaParams] = useState(false);

	const handleStartAnalysis = () => {
		if (!parameters.trim() || !prospectQueryId || count === 0) return;

		startAnalysisMutation.mutate(
			{
				prospectQueryId,
				criteriaText: parameters.trim(),
				analysisType: 'companyFit',
				includeSocialMedia: addSocialMediaParams,
				scope,
			},
			{ onSuccess: () => setIsOpen(false) },
		);
	};

	const handleToggleSocialMediaParameters = () => {
		setAddSocialMediaParams(!addSocialMediaParams);
	};

	const trigger = (
		<RequireCapability required="ai.companyAnalysis">
			<ActionScopeButton
				icon={
					<div className="relative inline-flex items-center justify-center">
						<span className="absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75 animate-[ping_1s_ease-in-out_infinite]"></span>
						<HiSparkles className="h-4 w-4 relative z-10" />
					</div>
				}
				onClick={() => setIsOpen(!isOpen)}
				disabled={hasContactSelection}
				title={hasContactSelection ? CONTACT_SELECTION_HINT : undefined}
			>
				KI-Analyse
			</ActionScopeButton>
		</RequireCapability>
	);

	return (
		<Popover open={isOpen} onClose={() => setIsOpen(false)} trigger={trigger} size="m" align="end">
			<Panel.Header icon={BsCpu} onClose={() => setIsOpen(false)}>
				KI-Analyse Konfiguration
			</Panel.Header>

			<Panel.Body>
				<p className="text-xs text-text mb-3">
					Definieren Sie die Parameter, anhand derer die KI die Webseiten der Unternehmen auf Sinnhaftigkeit
					und Leadqualität prüfen soll.
				</p>

				<FormField label="Analyse-Parameter" htmlFor="parameters">
					<Textarea
						className="min-h-[110px] resize-none"
						id="parameters"
						value={parameters}
						onChange={(e) => setParameters(e.target.value)}
						placeholder="Beschreiben Sie hier, worauf die KI bei der Analyse achten soll. Beispiel: Welche Firma sucht Personal? Wer fertigt auch für Privatkunden?"
					/>
				</FormField>

				<div className="mt-3">
					<Checkbox
						id="select-social-media-parameters"
						label="Social-Media Daten einbeziehen"
						onChange={handleToggleSocialMediaParameters}
						checked={addSocialMediaParams}
					/>
				</div>
			</Panel.Body>

			<Panel.Footer stacked>
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-2">
						<HiSparkles className="w-4 h-4 text-primary-500" />
						<span className="text-sm font-medium text-text">KI-Analyse</span>
					</div>

					<Button
						onClick={handleStartAnalysis}
						disabled={parameters.trim().length < 10 || count === 0 || startAnalysisMutation.isPending}
						variant="solid"
						size="md"
					>
						{startAnalysisMutation.isPending ? 'Starte Analyse...' : 'Analyse starten'}
					</Button>
				</div>
				{count > 0 && (
					<p className="text-xs text-bgBase-800 mt-2">
						Analysiert werden <strong>{leadActionTarget}</strong>.
					</p>
				)}
			</Panel.Footer>
		</Popover>
	);
};
